import React, { useCallback, useEffect, useState } from 'react';

import { Search } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import AddNewButton from '../../../components/common/AddButton';
import Drawer from '../../../components/common/Drawer';
import FormSection from '../../../components/common/FormSection';
import CountryForm from '../../../components/forms/CountryForm';
import StateForm from '../../../components/forms/StateForm';
import SubRegionForm from '../../../components/forms/SubRegionForm';
import AdminLayout from '../../../components/layout/AdminLayout';
import StateTable from '../../../components/tables/StateTable';
import SubRegionTable from '../../../components/tables/SubRegionTable';
import { API_BASE_URL } from '../../../config/config';
import { useCountryById, useUpdateCountry } from '../../../hooks/useCountryApi';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import {
  StateClient,
  StateDTO,
  SubRegionClient,
  SubRegionDTO,
} from '../../../services/apiClient';
import axiosInstance from '../../../services/axiosInstance';
import { confirmDelete } from '../../../utils/sweetAlertUtils';

const stateClient = new StateClient(API_BASE_URL, axiosInstance);
const subRegionClient = new SubRegionClient(API_BASE_URL, axiosInstance);

type DrawerView =
  | 'add-state'
  | 'edit-state'
  | 'add-subregion'
  | 'edit-subregion'
  | null;

const CountryEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: entityData } = useCountryById(id!);
  const updateCountry = useUpdateCountry();
  const navigate = useNavigate();

  const [states, setStates] = useState<StateDTO[]>([]);
  const [subRegions, setSubRegions] = useState<SubRegionDTO[]>([]);
  const [isStatesLoading, setIsStatesLoading] = useState(false);
  const [isSubRegionsLoading, setIsSubRegionsLoading] = useState(false);
  const [isDrawerSaving, setIsDrawerSaving] = useState(false);

  const [drawerView, setDrawerView] = useState<DrawerView>(null);
  const [selectedState, setSelectedState] = useState<StateDTO | null>(null);
  const [selectedSubRegion, setSelectedSubRegion] =
    useState<SubRegionDTO | null>(null);

  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [subRegionSearchTerm, setSubRegionSearchTerm] = useState('');
  const debouncedStateSearchTerm = useDebouncedValue(stateSearchTerm, 300);
  const debouncedSubRegionSearchTerm = useDebouncedValue(
    subRegionSearchTerm,
    300,
  );

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Countries', href: '/admin/countries' },
    { label: 'Edit', href: '#' },
  ];

  const fetchStates = useCallback(async () => {
    if (!id) {
      return;
    }
    setIsStatesLoading(true);
    try {
      const response = await stateClient.searchStates({
        page: 1,
        pageSize: 1000,
        sortBy: 'Name',
        sortDescending: false,
        filters: { countryId: id, name: debouncedStateSearchTerm || undefined },
      });
      setStates(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching States:', error);
    } finally {
      setIsStatesLoading(false);
    }
  }, [id, debouncedStateSearchTerm]);

  const fetchSubRegions = useCallback(async () => {
    if (!id) {
      return;
    }
    setIsSubRegionsLoading(true);
    try {
      const response = await subRegionClient.searchSubRegions({
        page: 1,
        pageSize: 1000,
        sortBy: 'Name',
        sortDescending: false,
        filters: {
          countryId: id,
          name: debouncedSubRegionSearchTerm || undefined,
        },
      });
      setSubRegions(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Sub Regions:', error);
    } finally {
      setIsSubRegionsLoading(false);
    }
  }, [id, debouncedSubRegionSearchTerm]);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  useEffect(() => {
    fetchSubRegions();
  }, [fetchSubRegions]);

  const handleCountrySubmit = async (data: any) => {
    try {
      await updateCountry.mutateAsync({ id: id!, data });
      navigate('/admin/countries');
    } catch (error) {
      console.error('Error updating Country:', error);
    }
  };

  const closeDrawer = () => {
    setDrawerView(null);
    setSelectedState(null);
    setSelectedSubRegion(null);
  };

  const handleSaveState = async (data: StateDTO) => {
    setIsDrawerSaving(true);
    try {
      if (data.id) {
        await stateClient.updateState(data.id, data);
      } else {
        await stateClient.createState(data);
      }
      closeDrawer();
      await fetchStates();
    } catch (error) {
      console.error('Error saving State:', error);
    } finally {
      setIsDrawerSaving(false);
    }
  };

  const handleDeleteState = async (row: StateDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed || !row.id) {
      return;
    }

    try {
      await stateClient.deleteState(row.id);
      await fetchStates();
    } catch (error) {
      console.error('Error deleting State:', error);
    }
  };

  const handleSaveSubRegion = async (data: SubRegionDTO) => {
    setIsDrawerSaving(true);
    try {
      if (data.id) {
        await subRegionClient.updateSubRegion(data.id, data);
      } else {
        await subRegionClient.createSubRegion(data);
      }
      closeDrawer();
      await fetchSubRegions();
    } catch (error) {
      console.error('Error saving Sub Region:', error);
    } finally {
      setIsDrawerSaving(false);
    }
  };

  const handleDeleteSubRegion = async (row: SubRegionDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed || !row.id) {
      return;
    }

    try {
      await subRegionClient.deleteSubRegion(row.id);
      await fetchSubRegions();
    } catch (error) {
      console.error('Error deleting Sub Region:', error);
    }
  };

  const stateOptions = states
    .filter((s) => s.id)
    .map((s) => ({ value: s.id as string, label: s.name || '' }));

  const subRegionRows = subRegions.map((sr) => ({
    ...sr,
    stateName: states.find((s) => s.id === sr.stateId)?.name || '',
  }));

  const drawerTitles: Record<Exclude<DrawerView, null>, string> = {
    'add-state': 'Add State',
    'edit-state': 'Edit State',
    'add-subregion': 'Add Sub Region',
    'edit-subregion': 'Edit Sub Region',
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/countries"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.Country.Edit"
    >
      {entityData ? (
        <>
          <CountryForm
            initialValues={entityData}
            onSubmit={handleCountrySubmit}
            onCancel={() => navigate('/admin/countries')}
          />

          <FormSection
            id="states"
            title="States"
            subtitle="States belonging to this country"
            headerActions={
              <AddNewButton
                label="Add State"
                onClick={() => {
                  setSelectedState(null);
                  setDrawerView('add-state');
                }}
              />
            }
          >
            <div className="relative mb-4 max-w-xs">
              <input
                type="search"
                value={stateSearchTerm}
                onChange={(e) => setStateSearchTerm(e.target.value)}
                placeholder="Search states..."
                aria-label="Search states"
                className="h-10 w-full rounded border border-gray-200 pl-9 pr-4 text-sm text-ink
                  dark:border-white/20 dark:bg-white/5 dark:text-white/80 outline-none transition-all
                  focus:border-secondary dark:focus:border-secondary"
              />
              <Search
                className="absolute left-2.5 top-2.5 h-5 w-5 text-ink-muted"
                aria-hidden="true"
              />
            </div>
            <StateTable
              data={states}
              isLoading={isStatesLoading}
              handleEdit={(row) => {
                setSelectedState(row);
                setDrawerView('edit-state');
              }}
              handleDelete={handleDeleteState}
            />
          </FormSection>

          <FormSection
            id="sub-regions"
            title="Sub Regions"
            subtitle="Sub regions belonging to this country"
            headerActions={
              <AddNewButton
                label="Add Sub Region"
                onClick={() => {
                  setSelectedSubRegion(null);
                  setDrawerView('add-subregion');
                }}
              />
            }
          >
            <div className="relative mb-4 max-w-xs">
              <input
                type="search"
                value={subRegionSearchTerm}
                onChange={(e) => setSubRegionSearchTerm(e.target.value)}
                placeholder="Search sub regions..."
                aria-label="Search sub regions"
                className="h-10 w-full rounded border border-gray-200 pl-9 pr-4 text-sm text-ink
                  dark:border-white/20 dark:bg-white/5 dark:text-white/80 outline-none transition-all
                  focus:border-secondary dark:focus:border-secondary"
              />
              <Search
                className="absolute left-2.5 top-2.5 h-5 w-5 text-ink-muted"
                aria-hidden="true"
              />
            </div>
            <SubRegionTable
              data={subRegionRows}
              isLoading={isSubRegionsLoading}
              handleEdit={(row) => {
                setSelectedSubRegion(row);
                setDrawerView('edit-subregion');
              }}
              handleDelete={handleDeleteSubRegion}
            />
          </FormSection>
        </>
      ) : (
        <p>Loading...</p>
      )}

      <Drawer
        isOpen={drawerView !== null}
        onClose={closeDrawer}
        title={drawerView ? drawerTitles[drawerView] : ''}
      >
        {(drawerView === 'add-state' || drawerView === 'edit-state') && id && (
          <StateForm
            initialValues={selectedState || ({} as StateDTO)}
            countryId={id}
            onSubmit={handleSaveState}
            onCancel={closeDrawer}
            isLoading={isDrawerSaving}
          />
        )}
        {(drawerView === 'add-subregion' || drawerView === 'edit-subregion') &&
          id && (
            <SubRegionForm
              initialValues={selectedSubRegion || ({} as SubRegionDTO)}
              countryId={id}
              stateOptions={stateOptions}
              onSubmit={handleSaveSubRegion}
              onCancel={closeDrawer}
              isLoading={isDrawerSaving}
            />
          )}
      </Drawer>
    </AdminLayout>
  );
};

export default CountryEdit;
